if (!customElements.get('hadiyah-bulk-pricing')) {
  customElements.define(
    'hadiyah-bulk-pricing',
    class HadiyahBulkPricing extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bulkPricingReady === 'true') return;
        this.dataset.bulkPricingReady = 'true';

        this.quantityInput = this.querySelector('[data-hadiyah-bulk-quantity]');
        this.decreaseButton = this.querySelector('[data-hadiyah-bulk-decrease]');
        this.increaseButton = this.querySelector('[data-hadiyah-bulk-increase]');
        this.basePrice = this.querySelector('[data-hadiyah-bulk-base-price]');
        this.price = this.querySelector('[data-hadiyah-bulk-price]');
        this.savings = this.querySelector('[data-hadiyah-bulk-savings]');
        this.subtotal = this.querySelector('[data-hadiyah-bulk-subtotal]');

        if (!this.quantityInput || !this.basePrice || !this.price || !this.subtotal) return;

        this.onProductFormSubmit = () => this.syncProductFormQuantity();
        this.onProductFormData = (event) => {
          event.formData.set('quantity', String(this.normaliseQuantity(this.quantityInput.value)));
          event.formData.set('properties[_minimum_quantity]', String(this.getMinimumQuantity()));
        };
        this.bindProductForm();

        this.decreaseButton?.addEventListener('click', () => this.stepQuantity(-1));
        this.increaseButton?.addEventListener('click', () => this.stepQuantity(1));
        this.quantityInput.addEventListener('input', () => {
          const maximum = Number(this.quantityInput.max) || 9999;
          if (Number(this.quantityInput.value) > maximum) {
            this.quantityInput.value = maximum;
          }
          this.updateSubtotal();
        });
        this.quantityInput.addEventListener('change', () => {
          this.quantityInput.value = this.normaliseQuantity(this.quantityInput.value);
          this.updateSubtotal();
        });

        this.basePriceObserver = new MutationObserver(() => this.updatePricing());
        this.basePriceObserver.observe(this.basePrice, {
          attributes: true,
          attributeFilter: ['data-money-cents'],
        });

        this.onCurrencyChange = () => this.updatePricing();
        document.addEventListener('display-currency:changed', this.onCurrencyChange);

        this.variantUpdateEventName = window.MainEvents?.variantUpdate || 'variant:update';
        this.onVariantUpdate = (event) => {
          const eventProductId = String(event.detail?.data?.productId || '');
          const productId = String(this.dataset.productId || '');
          if (eventProductId && productId && eventProductId !== productId) return;

          const variantPrice = Number(event.detail?.resource?.price);
          if (!Number.isFinite(variantPrice)) return;

          this.updateMoneyElement(this.basePrice, variantPrice);
          this.updatePricing();
        };
        document.addEventListener(this.variantUpdateEventName, this.onVariantUpdate);

        this.updatePricing();
      }

      disconnectedCallback() {
        this.basePriceObserver?.disconnect();
        document.removeEventListener('display-currency:changed', this.onCurrencyChange);
        document.removeEventListener(this.variantUpdateEventName, this.onVariantUpdate);
        this.unbindProductForm();
      }

      bindProductForm() {
        const productForm = document.getElementById(this.dataset.productFormId);
        if (productForm === this.productForm) return;

        this.unbindProductForm();
        this.productForm = productForm;
        this.productForm?.addEventListener('submit', this.onProductFormSubmit, { capture: true });
        this.productForm?.addEventListener('formdata', this.onProductFormData);
      }

      unbindProductForm() {
        this.productForm?.removeEventListener('submit', this.onProductFormSubmit, { capture: true });
        this.productForm?.removeEventListener('formdata', this.onProductFormData);
        this.productForm = null;
      }

      normaliseQuantity(value) {
        const minimum = this.getMinimumQuantity();
        const maximum = Math.max(minimum, Number(this.quantityInput.max) || 9999);
        const quantity = Math.round(Number(value) || minimum);
        return Math.min(maximum, Math.max(minimum, quantity));
      }

      getMinimumQuantity() {
        return Math.max(
          1,
          Math.round(Number(this.dataset.minimumQuantity) || Number(this.quantityInput.min) || 1)
        );
      }

      stepQuantity(direction) {
        this.quantityInput.value = this.normaliseQuantity(
          Number(this.quantityInput.value) + direction
        );
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      getBulkDisplayPrice(basePrice, quantity) {
        const bulkEstimatedCostRatio = 0.53035452;
        const bulkPricingQuantityLimit = 500;
        const wholeCurrencyUnitInCents = 100;
        const minimumQuantity = this.getMinimumQuantity();
        const pricingQuantity = Math.min(
          Math.max(1, quantity - minimumQuantity + 1),
          bulkPricingQuantityLimit
        );
        const quantityFactor = 1.159821 * pricingQuantity ** -0.1380503;
        const estimatedPrice =
          basePrice *
          (bulkEstimatedCostRatio + (1 - bulkEstimatedCostRatio) * quantityFactor);
        const wholeCurrencyPrice =
          Math.ceil(estimatedPrice / wholeCurrencyUnitInCents) * wholeCurrencyUnitInCents;

        return Math.min(basePrice, wholeCurrencyPrice);
      }

      updatePricing() {
        const basePrice = Number(this.basePrice.dataset.moneyCents) || 0;
        const quantity = this.normaliseQuantity(this.quantityInput.value);
        const unitPrice = this.getBulkDisplayPrice(basePrice, quantity);
        const subtotalCents = unitPrice * quantity;
        const savingsPercentage =
          basePrice > 0 ? Math.max(0, Math.round(((basePrice - unitPrice) * 100) / basePrice)) : 0;

        this.updateButtonStates(quantity);
        this.syncProductFormQuantity(quantity);
        this.basePrice.hidden = unitPrice >= basePrice;
        this.price.classList.toggle('is-discounted', unitPrice < basePrice);
        this.updateMoneyElement(this.price, unitPrice);
        this.updateMoneyElement(this.subtotal, subtotalCents);

        if (this.savings) {
          this.savings.hidden = savingsPercentage < 1;
          this.savings.textContent = savingsPercentage > 0 ? `Save ${savingsPercentage}% per item` : '';
        }
      }

      syncProductFormQuantity(quantity = this.normaliseQuantity(this.quantityInput.value)) {
        this.bindProductForm();
        if (!this.productForm) return;

        Array.from(this.productForm.elements).forEach((control) => {
          if (control === this.quantityInput || control.name !== 'quantity') return;
          control.value = String(quantity);
        });
      }

      updateSubtotal() {
        this.updatePricing();
      }

      updateMoneyElement(element, cents) {
        const baseDisplay = this.formatBaseMoney(cents);

        element.dataset.moneyCents = String(cents);
        element.dataset.baseCurrency = 'INR';
        element.dataset.baseDisplay = baseDisplay;
        element.dataset.inrDisplay = baseDisplay;
        element.textContent = baseDisplay;
        element.removeAttribute('data-display-currency-converted');
        element.removeAttribute('title');

        window.HadiyahDisplayCurrency?.refresh?.(element);
      }

      updateButtonStates(quantity) {
        const minimum = this.normaliseQuantity(this.quantityInput.min);
        const maximum = Number(this.quantityInput.max) || 9999;

        if (this.decreaseButton) this.decreaseButton.disabled = quantity <= minimum;
        if (this.increaseButton) this.increaseButton.disabled = quantity >= maximum;
      }

      formatBaseMoney(cents) {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      }
    }
  );
}
